import type { FC } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineConfirmationNumber } from 'react-icons/md';
import { useTheme } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { getCurrentLocale } from 'src/helper/helper';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import GroupedBarChartWidget from 'src/components/followUp/common/BaseWidgets/GroupedBarChartWidget';
import type { StackedNumbersList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetFooterLegend from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { useExportOptions } from './hooks';

const AverageResolutionTimeWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const { t } = useTranslation('followUp');
  const { colors } = useTheme();

  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.AVERAGE_RESOLUTION_TIME,
    config.id,
  );

  const { data, isFetching, errorMsg, refetch, queryFilters, comparisonData } =
    useWidgetQuery<StackedNumbersList>(endpoint);

  const stackLabels = [
    {
      name: t(
        'statistics.averageResolutionTimeWidget.labels.createdToFirstContact',
      ),
      color: colors.secondary[500],
    },
    {
      name: t('statistics.averageResolutionTimeWidget.labels.createdToClosed'),
      color: colors.secondary[700],
    },
  ];
  const locale = getCurrentLocale();

  const exportOptions = useExportOptions({
    stackLabels,
    data: data ?? null,
    comparisonData: comparisonData ?? null,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      error={errorMsg}
      loading={isFetching}
      exportOptions={exportOptions}
      header={
        <WidgetHeaderWithPill
          title={config.name}
          pillProps={{
            label: (data?.totalObjects ?? 0).toLocaleString(locale),
            icon: <MdOutlineConfirmationNumber />,
            color: 'primary',
          }}
          ariaId={ariaId}
        />
      }
      footer={
        data?.totalObjects === 0 ? (
          <NoResultsToShowAdjustFiltersWarning />
        ) : (
          <WidgetFooterLegend labels={stackLabels} />
        )
      }
    >
      {data && (
        <GroupedBarChartWidget
          bars={
            data.totalObjects
              ? data.results.map((bar) => ({
                  label: t(`npsGroup.${bar.label}`),
                  stacks: bar.stacks.map((stack) => ({
                    ...stack,
                    stackLabel: t(
                      `statistics.averageResolutionTimeWidget.labels.${stack.stackLabel}`,
                    ),
                  })),
                }))
              : []
          }
          stackLabels={stackLabels}
          yAxisLabel={t('statistics.averageResolutionTimeWidget.yAxisLabel')}
        />
      )}
    </WidgetFrame>
  );
};

export default AverageResolutionTimeWidget;
